/**
 * SourceFolderPicker
 *
 * A folder-tree browser that lets the user navigate their full OneDrive
 * (starting from the OneDrive root) and SELECT a folder as a source for import.
 * Similar to MoveFolderPicker but starts at the OneDrive root, never excludes
 * any folder, and calls onSelect({ id, name }) instead of onMove().
 */
import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, FolderOpen, Check, ArrowsClockwise } from "@phosphor-icons/react";
import { getDriveRoot, listFolder } from "../../api/microsoft";

async function fetchChildren(folderId) {
  const items = await listFolder(folderId);
  return items.filter((item) => item.folder);
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.detail || error?.message || fallback;
}

export default function SourceFolderPicker({ onSelect, onClose }) {
  const [root,       setRoot]       = useState(null);
  const [stack,      setStack]      = useState([]); // [{id, name}, ...]
  const [folders,    setFolders]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const current = stack.length > 0 ? stack[stack.length - 1] : root;

  const load = useCallback(async (folderId) => {
    setLoading(true);
    setError(null);
    try {
      const children = await fetchChildren(folderId);
      setFolders(children);
    } catch (e) {
      setError(getErrorMessage(e, "Could not load this OneDrive folder."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load OneDrive root on mount
  useEffect(() => {
    (async () => {
      try {
        const rootItem = await getDriveRoot();
        setRoot(rootItem);
        await load(rootItem.id);
      } catch (e) {
        setError(getErrorMessage(e, "Could not load OneDrive root."));
        setLoading(false);
      }
    })();
  }, [load]);

  const handleOpen = (folder) => {
    setStack((s) => [...s, { id: folder.id, name: folder.name }]);
    load(folder.id);
  };

  const handleBack = () => {
    setStack((s) => {
      const next = s.slice(0, -1);
      const parent = next.length > 0 ? next[next.length - 1] : root;
      if (parent) load(parent.id);
      return next;
    });
  };

  const handleSelect = () => {
    if (current) onSelect({ id: current.id, name: current.name });
  };

  const breadcrumb = [
    { id: root?.id, name: root?.name || "My OneDrive" },
    ...stack,
  ];

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="source-folder-picker"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="source-folder-picker-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <FolderOpen size={15} className="text-violet-500" weight="fill" />
            <h3 id="source-folder-picker-title" className="text-sm font-bold text-slate-800">
              Select source folder
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xs"
            data-testid="source-picker-close"
            aria-label="Close source folder picker"
          >
            ✕
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="px-4 py-2 flex items-center gap-1 flex-wrap border-b border-slate-100 bg-white">
          {breadcrumb.map((b, i) => (
            <React.Fragment key={b.id || "root"}>
              {i > 0 && <span className="text-slate-300 text-xs">/</span>}
              <span className="text-xs text-slate-500 truncate max-w-[120px]">{b.name}</span>
            </React.Fragment>
          ))}
        </div>

        {/* Back + folder list */}
        <div className="flex-1 overflow-y-auto max-h-72">
          {stack.length > 0 && (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50 border-b border-slate-100 transition-colors"
              data-testid="source-picker-back"
            >
              <ArrowLeft size={13} />
              <span>Back</span>
            </button>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8" role="status" aria-label="Loading OneDrive folders">
              <ArrowsClockwise size={20} className="animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <p className="text-xs text-red-500 px-4 py-6 text-center" role="alert">{error}</p>
          ) : folders.length === 0 ? (
            <p className="text-xs text-slate-400 px-4 py-6 text-center" role="status">
              No sub-folders here.
              <br />
              You can still select this folder.
            </p>
          ) : (
            folders.map((f) => (
              <button
                key={f.id}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700 transition-colors group"
                onClick={() => handleOpen(f)}
                data-testid={`source-folder-option-${f.name}`}
              >
                <FolderOpen size={14} className="text-amber-400 flex-shrink-0" weight="fill" />
                <span className="truncate flex-1 text-left">{f.name}</span>
                <span className="text-[10px] text-slate-400 opacity-0 group-hover:opacity-100">
                  Open →
                </span>
              </button>
            ))
          )}
        </div>

        {/* Select current folder */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500 truncate flex-1">
            Select: <span className="font-medium text-slate-700">{current?.name}</span>
          </p>
          <button
            onClick={handleSelect}
            disabled={!current}
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 px-3 py-1.5 rounded transition-colors"
            data-testid="source-folder-select-btn"
          >
            <Check size={12} weight="bold" />
            Select this folder
          </button>
        </div>
      </div>
    </div>
  );
}
